/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { collection as collectionBase, type ICollection } from 'libx'
import { type Model, type ModelProps } from './model'
import { action, observe, type IArrayUpdate, type IArraySplice } from 'mobx'
import { Observable, from, filter } from 'rxjs'
import { concatMap, share } from 'rxjs/operators'

/**
 * Collection options.
 */
export interface CollectionOptions<
  T extends Model<unknown>,
  I = string,
  P extends ModelProps<T> = ModelProps<T>,
> {
  /**
   * Called when the collection wants to add a new item.
   */
  create: (attributes: Partial<P>) => T
  /**
   * Called when the collection wants to update an existing item with more data.
   */
  update?: (existing: T, input: Partial<P>) => T
  /**
   * Used to get the ID of a model that was passed in. Whatever is returned from
   * this should be coercible to a string, and is used for indexing.
   */
  getModelId: (obj: T, opts: CollectionOptions<T, I, P>) => I | undefined
  /**
   * Used to get an ID from input data, used to determine whether to create
   * or update.
   */
  getDataId: (
    attrs: Partial<P>,
    opts: CollectionOptions<T, I, P>,
  ) => I | undefined
}

/**
 * If `T` is an array of `I`, the resulting type is `I`, otherwise it is `T`.
 */
type UnwrapArray<T> = T extends (infer I)[] ? I : T

/**
 * Model collection.
 */
export type Collection<
  T extends Model<unknown>,
  P extends ModelProps<T>,
> = Omit<ICollection<T>, 'create' | 'update' | 'set' | 'reset'> & {
  /**
   * Multi-version of `create`.
   */
  create(data: Partial<P>[]): T[]
  /**
   * Like `set`, but will add regardless of whether an id is present or not.
   * This has the added risk of resulting multiple model instances if you don't make sure
   * to update the existing model once you do have an id. The model id is what makes the whole
   * one-instance-per-entity work.
   */
  create(data: Partial<P>): T
  /**
   * Same as the singular version, but with multiple.
   */
  set<
    D extends (Partial<P> | null | undefined)[] | undefined,
    R = T | Exclude<UnwrapArray<D>, Partial<P>>,
  >(
    data: D,
  ): D extends undefined ? R[] | undefined : R[]
  /**
   * Adds a single item and maps it using the mapper in the options.
   */
  set<D extends Partial<P> | null | undefined>(
    data: D,
  ): T | Exclude<D, Partial<P>>
  /**
   * Adds elements that were not in the collection, updates elements that are, and removes elements that are not
   * represented in `data` (using `getDataId`).
   *
   * @param data the data to reset to. If undefined, does nothing.
   */
  reset(data?: Partial<P>[]): Collection<T, P>
  /**
   * An observable that emits added values.
   */
  additions$: Observable<T>
  /**
   * An observable that emits removed values.
   */
  removals$: Observable<T>
}

/**
 * Creates a Collection.
 * @param collectionOpts
 */
export function collection<
  T extends Model<unknown>,
  I = string,
  P extends ModelProps<T> = ModelProps<T>,
>(collectionOpts: CollectionOptions<T, I, P>): Collection<T, P> {
  // @ts-ignore
  const collectionInstance = collectionBase<T>({
    ...collectionOpts,
    create: (attributes) => {
      const instance = collectionOpts.create(attributes)
      instance.set(attributes)
      return instance
    },
    update: (existing, input) => existing.set(input),
  }) as Collection<T, P>

  // This is used to make sure we don't emit double additions/removals when resetting the internal collection
  // array in reset([])
  let silenceEvents = false
  collectionInstance.reset = action((data?: Partial<P>[]) => {
    if (!data) {
      return collectionInstance
    }
    const getDataId = collectionOpts.getDataId
    const getModelId = collectionOpts.getModelId
    const dataMap = new Map(
      data.map((d) => [getDataId(d, collectionOpts), true]),
    )
    const itemsBeforeSet = collectionInstance.slice()
    const newItems: T[] = collectionInstance.set(data)
    // Remove elements that are no longer there.
    // Go through the proper method to make sure the internal ID cache is
    // updated.
    itemsBeforeSet.forEach((model) => {
      // @ts-ignore
      const isInData = dataMap.has(getModelId(model, collectionOpts))
      if (!isInData) {
        collectionInstance.remove(model)
      }
    })

    // Replace the internal array with the new items to get the proper ordering.
    silenceEvents = true
    collectionInstance.items.replace(newItems)
    silenceEvents = false
    return collectionInstance
  })

  const changes$ = new Observable<IArrayUpdate | IArraySplice>((observer) => {
    const dispose = observe(
      collectionInstance.items,
      observer.next.bind(observer),
    )
    return () => dispose()
  }).pipe(
    filter((change) => change.type === 'splice' && !silenceEvents),
    share(),
  )

  collectionInstance.removals$ = changes$.pipe(
    concatMap((change) =>
      from((change.type === 'splice' ? change.removed : []) as T[]),
    ),
    share(),
  )

  collectionInstance.additions$ = changes$.pipe(
    concatMap((change) =>
      from((change.type === 'splice' ? change.added : []) as T[]),
    ),
    share(),
  )

  return collectionInstance
}
